// src/utils/recurringTransfers.js
//
// Recurring account-to-account transfers.
//
// A recurring transfer is a SCHEDULE stored in data.recurringTransfers; the
// transfers it produces are ordinary ledger entries in data.transfers,
// created through the exact same balance-adjusting path as manual transfers.
// Completed history is therefore never touched when a schedule is paused,
// edited or deleted — only future generation changes.
//
// Schedule shape (all dates are "YYYY-MM-DD" calendar dates):
//   {
//     id, fromAccountId, toAccountId, amount, note,
//     frequency:    'weekly' | 'bi-weekly' | 'monthly' | 'quarterly' | 'annual',
//     startDate,                  // anchor date of the first occurrence
//     endDate,                    // optional — no occurrences after this date
//     maxOccurrences,             // optional — stop after N postings
//     paused,                     // true = temporarily generate nothing
//     skippedDates,               // occurrence dates the user undid — never
//                                 // re-posted (see DELETE_TRANSFER)
//     createdAt
//   }
//
// Duplicate protection: every generated transfer gets the deterministic id
// `rt-<scheduleId>-<occurrenceDate>`. Posting scans the ledger for that id
// first, so re-running generation (every app load, after imports, etc.) can
// never double-post an occurrence or double-adjust account balances.

import { getOccurrencesInRange, toDateString } from './recurrence';

export const TRANSFER_FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Yearly' }
];

/** Deterministic ledger id for one occurrence of a schedule. */
export function getOccurrenceId(scheduleId, dateStr) {
  return `rt-${scheduleId}-${dateStr}`;
}

/** Today as a "YYYY-MM-DD" local calendar date. */
export function todayString(today = new Date()) {
  return toDateString(today.getFullYear(), today.getMonth() + 1, today.getDate());
}

/**
 * All occurrence dates a schedule produces in [rangeStart, rangeEnd]
 * (inclusive), respecting startDate, endDate and maxOccurrences. Paused
 * schedules produce nothing.
 */
export function getScheduleOccurrences(schedule, rangeStart, rangeEnd) {
  if (!schedule || schedule.paused) return [];
  if (!schedule.startDate) return [];

  // Clamp the query range to the schedule's own active window.
  const start = rangeStart && rangeStart > schedule.startDate ? rangeStart : schedule.startDate;
  let end = rangeEnd;
  if (schedule.endDate && (!end || schedule.endDate < end)) end = schedule.endDate;
  if (!end || start > end) return [];

  // The recurrence engine projects anchors both forwards and backwards, so a
  // pseudo-expense with the schedule's start date as its anchor reproduces
  // exactly the pay-cadence walk used everywhere else in the app.
  const occurrences = getOccurrencesInRange(
    { date: schedule.startDate, recurrenceType: schedule.frequency },
    start,
    end
  );

  const max = parseInt(schedule.maxOccurrences, 10);
  if (Number.isFinite(max) && max > 0) {
    // maxOccurrences counts from the schedule start, not the query range.
    const allFromStart = getOccurrencesInRange(
      { date: schedule.startDate, recurrenceType: schedule.frequency },
      schedule.startDate,
      end
    );
    const allowed = new Set(allFromStart.slice(0, max));
    return occurrences.filter(d => allowed.has(d));
  }

  return occurrences;
}

/**
 * The transfers that are DUE (occurrence date ≤ today) but not yet posted to
 * the ledger. Returns ledger-ready transfer objects; the caller applies them
 * through the normal ADD_TRANSFER balance logic.
 *
 * @param {Array} schedules  data.recurringTransfers
 * @param {Array} transfers  data.transfers (existing ledger, for dedupe)
 * @param {Array} accounts   data.accounts (occurrences whose accounts no
 *                           longer exist are skipped, never posted blindly)
 * @param {Date}  [today]
 */
export function getDueTransfers(schedules, transfers, accounts, today = new Date()) {
  const ledgerIds = new Set((Array.isArray(transfers) ? transfers : []).map(t => t.id));
  const accountIds = new Set((Array.isArray(accounts) ? accounts : []).map(a => a.id));
  const todayStr = todayString(today);
  const due = [];

  (Array.isArray(schedules) ? schedules : []).forEach(schedule => {
    if (!schedule || !schedule.id) return;
    if (!accountIds.has(schedule.fromAccountId) || !accountIds.has(schedule.toAccountId)) return;
    // Occurrences the user explicitly undid must stay gone.
    const skipped = new Set(Array.isArray(schedule.skippedDates) ? schedule.skippedDates : []);

    getScheduleOccurrences(schedule, schedule.startDate, todayStr).forEach(dateStr => {
      if (skipped.has(dateStr)) return;
      const id = getOccurrenceId(schedule.id, dateStr);
      if (ledgerIds.has(id)) return;
      due.push({
        id,
        recurringTransferId: schedule.id,
        fromAccountId: schedule.fromAccountId,
        toAccountId: schedule.toAccountId,
        amount: parseFloat(schedule.amount) || 0,
        date: dateStr,
        note: schedule.note || '',
        createdAt: new Date().toISOString()
      });
    });
  });

  // Oldest first so the ledger reads chronologically.
  return due.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/**
 * UPCOMING (future-dated) occurrences for display — e.g. the Planner's
 * "Upcoming transfers" list. Never posted; purely informational.
 *
 * @returns {Array<{ schedule, date, amount }>} sorted by date.
 */
export function getUpcomingTransfers(schedules, accounts, rangeStart, rangeEnd) {
  const accountIds = new Set((Array.isArray(accounts) ? accounts : []).map(a => a.id));
  const upcoming = [];

  (Array.isArray(schedules) ? schedules : []).forEach(schedule => {
    if (!schedule || !schedule.id) return;
    if (!accountIds.has(schedule.fromAccountId) || !accountIds.has(schedule.toAccountId)) return;
    getScheduleOccurrences(schedule, rangeStart, rangeEnd).forEach(date => {
      upcoming.push({ schedule, date, amount: parseFloat(schedule.amount) || 0 });
    });
  });

  return upcoming.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Human-readable summary of a schedule's cadence and end condition. */
export function describeSchedule(schedule) {
  const freq = TRANSFER_FREQUENCIES.find(f => f.value === schedule?.frequency);
  const cadence = freq ? freq.label : (schedule?.frequency || '');
  if (schedule?.endDate) return `${cadence} until ${schedule.endDate}`;
  const max = parseInt(schedule?.maxOccurrences, 10);
  if (Number.isFinite(max) && max > 0) return `${cadence}, ${max} time${max === 1 ? '' : 's'}`;
  return `${cadence}, never expires`;
}
